import * as React from "react";
import { Link, useParams } from "react-router-dom";
import { useFormApi, Form } from "informed";
import * as moment from "moment";
import "moment-duration-format";
import { useCurrentStudyConfigurationContext } from "../Core/CurrentStudyConfigurationContext";
import DocumentTitle from "../misc/components/DocumentTitle";
import Alert, { AlertCategoryEnum } from "../misc/components/Alert";
import { SubjectFormBody } from "./Forms/SubjectFormBody";
import { SubjectEdiaryFormBody } from "./Forms/SubjectEdiaryFormBody";
import { ActionButtons } from "../misc/ActionButtons";
import { ExtractDefaultAlarms, SubjectFormModel } from "./SubjectsShared";
import LoadingWithMessage from "../misc/components/LoadingWithMessage";
import { AuditModal, AuditForm } from "../auditmodal/components/AuditModal";
import { SessionTypes } from "../misc/api/ApiSessionTypes";
import { Site, SubjectMutation, WebPortalClient } from "webportalclient";
import { ConfigurableDataItemIds } from "../misc/api/ApiConfigurableDataItems";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../misc/api/WebPortalTokenClient";
import { DeviceTypes, Language } from "../misc/api/apiTypes";
import { useHasPermission } from "../misc/components/Authorization";
import { PermissionEnum } from "../misc/api/ApiPermissionEnum";

interface EditSubjectFormProps {
    subject: SubjectFormModel;
}

export const EditSubjectForm = ({ subject }: EditSubjectFormProps) => {
    const { study, dataItemConfigurationCollection, diaryConfigFiles } = useCurrentStudyConfigurationContext();
    const formApi = useFormApi<SubjectFormModel>();
    const [successAlert, setSuccessAlert] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);
    const [warningAlert, setWarningAlert] = React.useState<string>(null);
    const [errorAlert, setErrorAlert] = React.useState<string>(null);
    const [auditModalVisible, setAuditModalVisible] = React.useState(false);
    const [sites, setSites] = React.useState<Site[]>([]);
    const [subjectLimitedLanguages, setSubjectLimitedLanguages] = React.useState<Language[]>([]);
    const legacyClient = useWebPortalLegacyClient();
    const client = useWebPortalApiClient();
    const hasSubjectDemographicsPermission = useHasPermission(PermissionEnum.EditSubjectDemographics);

    React.useEffect(() => {
        const loadInitial = async () => {
            const sitesResult = await client.client.getSites(undefined, `studyId eq ${study.id}`);
            setSites(sitesResult.value);
        }

        loadInitial();
    }, [study.id]);

    const onPreAuditSubmit = async () => {
        setSubmitting(true);
        formApi.validate();
        let state = formApi.getState();

        if (state.values.BirthDate && (state.values.Age === null || state.values.Age === undefined)) {
            var age = await legacyClient.getCalculateAge(state.values.BirthDate);
            formApi.setValue('Age', age);
            formApi.validate()
            state = formApi.getState();
        }

        if (state.invalid) {
            setSubmitting(false);
            return setErrorAlert("Errors exist on the page");
        }

        if (state.values.Pin !== state.values.PinConfirmation) {
            setSuccessAlert(false);
            setSubmitting(false);
            return setErrorAlert("PIN does not match the PIN Confirmation");
        }

        setSubmitting(false);
        setAuditModalVisible(true);
    }

    const onSubmit = async (auditForm: AuditForm) => {
        setSubmitting(true);
        setSuccessAlert(false);
        setWarningAlert(null);
        setErrorAlert(null);

        formApi.validate();
        const state = formApi.getState();

        if (state.invalid) {
            setSubmitting(false);
            setAuditModalVisible(false);
            return setErrorAlert("Errors exist on the page");
        }

        const sitesResult = await legacyClient.getSites({ Filters: [] });
        const site = sitesResult.Data.find(s => s.Id === state.values.SiteId);
        const subjectIdConfiguration = dataItemConfigurationCollection.find(x => x.configurableDataItemId === ConfigurableDataItemIds.SubjectId);

        const apiForm = {
            ...state.values,
            StudyId: study.id,
            SubjectNumber: subjectIdConfiguration.prefixWithSiteNumber ? `${site.SiteNumber}${subjectIdConfiguration.prefixDelimiter}${state.values.SubjectNumber}` : state.values.SubjectNumber,
            BedTime: state.values.BedTime ? state.values.BedTime : undefined,
            EdiarySubject: state.values.Pin ? {
                Pin: state.values.Pin,
                Alarms: state.values.Alarms ?? [],
            } : undefined
        };

        try {
            const editedSubject = await client.client.patchSubjects({
                siteId: apiForm.SiteId,
                populationGroupId: apiForm.PopulationGroupId,
                number: apiForm.SubjectNumber,
                language: apiForm.LanguageId,
                gender: apiForm.GenderId,
                dateOfBirth: apiForm.BirthDate,
                age: apiForm.Age,
                height: apiForm.Height,
                weight: apiForm.Weight,
                status: apiForm.SubjectTypeId,
                bmi: apiForm.BMI,
                smokerType: apiForm.SmokerTypeId,
                bedTime: moment.duration(apiForm.BedTime).toISOString(),
                pin: apiForm.EdiarySubject ? apiForm.EdiarySubject.Pin : null,
                alarms: apiForm.EdiarySubject ? apiForm.EdiarySubject.Alarms.map(a => ({ time: moment.duration(a).toISOString() })) : [],
                race: apiForm.Race

            } as SubjectMutation, subject.Id, auditForm.Annotation);

            if (editedSubject) {
                setSuccessAlert(true); 

                if (study.sessionTypeIds.find(x => x === SessionTypes.EDiary)) {
                    const files = diaryConfigFiles;
                    if (files !== null && files !== undefined && editedSubject.language !== null) {
                        const languageLocaleName = editedSubject.language;

                        const stringFile = files.find(x => x.name.includes(languageLocaleName));
                        if (stringFile === null || stringFile === undefined) {
                            setWarningAlert(`The Diary will be configured with the default language as there is no matching translation for the selected language ‘${editedSubject.languageName}’`);
                        }
                    }
                }
            }
        } catch (ex) {
            setErrorAlert(ex.message);
        }

        setAuditModalVisible(false);
        setSubmitting(false);
    };

    return (
        <>
            <Alert show={successAlert} category={AlertCategoryEnum.Success}>
                Saved successfully. Return to{" "}
                <Link to={`/Subjects`}>Subjects list</Link>?
            </Alert>
            <Alert show={warningAlert !== null} category={AlertCategoryEnum.Warning}>
                {warningAlert}
            </Alert>
            <Alert show={errorAlert !== null} category={AlertCategoryEnum.Danger}>
                {errorAlert}
            </Alert>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Edit Subject</h2>
                </div>
                <div className="col-md-6">
                    <ActionButtons submitting={submitting} onSaveClick={() => onPreAuditSubmit()} automationIdPrefix="ActionsHeader" returnToListRoute="/subjects" />
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div className="panel panel-primary">
                        <div className="panel-heading">
                            <h4>Demographics</h4>
                        </div>
                        <div className="panel-body">
                            <SubjectFormBody isEdit={true} canEdit={hasSubjectDemographicsPermission} sites={sites} onSetLimitedLanguages={setSubjectLimitedLanguages} subjectLimitedLanguages={subjectLimitedLanguages} />
                        </div>
                    </div>
                    {study.deviceTypeIds.find(dt => dt === DeviceTypes.In2itiveEDiary || dt === DeviceTypes.In2itiveP230EDiary) !== undefined ?
                        <div className="panel panel-primary">
                            <div className="panel-heading">
                                <h4>eDiary Setup</h4>
                            </div>
                            <div className="panel-body">
                                <SubjectEdiaryFormBody sites={sites} enabled={true} onSetLimitedLanguages={setSubjectLimitedLanguages} subjectLimitedLanguages={subjectLimitedLanguages} />
                            </div>
                        </div> : null}
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <ActionButtons submitting={submitting} onSaveClick={() => onPreAuditSubmit()} automationIdPrefix="ActionsHeader" returnToListRoute="/subjects" />
                </div>
            </div>
            {auditModalVisible ? (
                <AuditModal
                    header="Approval"
                    isSubmitting={submitting}
                    onClose={() => setAuditModalVisible(false)}
                    onSubmit={async (audit) => await onSubmit(audit)}
                />
            ) : null}
        </>
    );
}

export const EditSubjectScreen = () => {
    const { id } = useParams<{ id: string }>();
    const [subject, setSubject] = React.useState<SubjectFormModel>();
    const client = useWebPortalApiClient();
    const { diarySettings } = useCurrentStudyConfigurationContext();

    React.useEffect(() => {
        const getData = async () => {
            if (!diarySettings) {
                return;
            }
            const receivedSubject = await client.client.getSubject(id);
            const site = await client.client.getSite(receivedSubject.siteId);

            const alarmString = diarySettings.DefaultEdiaryAlarm;

            const alarmTimes = alarmString ? ExtractDefaultAlarms(alarmString) : null;

            const formSubject: SubjectFormModel = {
                Id: receivedSubject.id,
                SubjectNumber: receivedSubject.number,
                StudyId: site.studyId,
                SiteId: site.id,
                SiteNumber: site.number,
                Age: receivedSubject.age ?? null,
                BirthDate: receivedSubject.dateOfBirth,
                GenderId: receivedSubject.gender,
                Height: receivedSubject.height ?? null,
                Weight: receivedSubject.weight ?? null,
                BMI: receivedSubject.bmi ?? null,
                PopulationGroupId: receivedSubject.populationGroupId,
                SmokerTypeId: receivedSubject.smokerType,
                LanguageId: receivedSubject.language,
                SubjectTypeId: receivedSubject.status,
                Pin: receivedSubject.pin,
                PinConfirmation: receivedSubject.pin,
                BedTime: receivedSubject.bedTime ? moment.duration(receivedSubject.bedTime).format("hh:mm:ss", {trim: false}) : null,
                Alarms: receivedSubject.alarms && receivedSubject.alarms.length > 0 ? receivedSubject.alarms.map(a => moment.duration(a.time).format("hh:mm:ss", { trim: false }))
                    : (diarySettings.DefaultEdiaryAlarm ? alarmTimes : null),
                Race: receivedSubject.race
            }

            setSubject(formSubject);
        }

        getData();
    }, [id, diarySettings]);

    if (!subject) return (<LoadingWithMessage message="Loading Subject..." />);

    return (
        <DocumentTitle title="Edit Subject | Vitalograph Web Portal">
        <Form noValidate initialValues={subject}>
            <EditSubjectForm subject={subject} />
        </Form>
        </DocumentTitle>
    );
}
